import React, { createContext, useContext, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportedLanguage, supportedLanguages } from '@/i18n';
import { useNavigate, useLocation } from 'react-router-dom';

type Language = SupportedLanguage;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t: i18nT, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const language = (supportedLanguages.includes(i18n.language as SupportedLanguage)
    ? i18n.language
    : 'en') as Language;

  const setLanguage = (lang: Language) => {
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    
    // Update URL to reflect new locale using React Router navigate
    const currentPath = location.pathname;
    const localePattern = /^\/(en|es|pt|fr)(\/|$)/;
    const match = currentPath.match(localePattern);
    
    let newPath: string;
    if (match) {
      // Replace existing locale prefix
      newPath = currentPath.replace(localePattern, `/${lang}$2`);
    } else {
      // Add locale prefix (for legacy URLs or admin pages — keep as-is for admin)
      if (currentPath.startsWith('/admin')) {
        return; // Don't change URL for admin routes
      }
      newPath = `/${lang}${currentPath}`;
    }
    
    navigate(newPath + location.search + location.hash, { replace: true });
  };

  const t = (key: string): string => {
    return i18nT(key);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
